import React, { useEffect } from "react";

import { Button } from "@epignosis_llc/gnosis";

import { popupListContainer } from "./styles";

type PopupChecklistProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

const PopupChecklist: React.FC<PopupChecklistProps> = ({ isOpen, onClose, children }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const doesContainInjectedElements = React.useCallback(() => {
    const popupContent = document.querySelector(".popup-content");

    if (!popupContent) return false;

    const children = popupContent.children;

    const shadowRoot = Array.from(children).find(
      (child) => child instanceof HTMLElement && child.shadowRoot,
    )?.shadowRoot;

    if (!shadowRoot) return false;

    const injectedElements = shadowRoot.querySelectorAll(".productfruits--checklist-panel");

    return injectedElements.length !== 0;
  }, []);

  const shouldShowElement = isOpen && doesContainInjectedElements();

  // Add global event listener to close popup when clicking anywhere
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent): void => {
      if (!containerRef.current || !shouldShowElement) return;

      /**
       * If clicked element is the toggle button, do nothing
       * This is to prevent from re-opening the popup when clicking on the toggle button
       * after closing it
       */

      if (!(event.target instanceof HTMLElement)) return;

      const parentElement = event.target.closest("#pf-checklist-toggle-button");

      if (parentElement) {
        return;
      }

      // Check if click is outside the popup container
      if (!containerRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add event listener if popup is open
    if (shouldShowElement) {
      document.addEventListener("mousedown", handleGlobalClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleGlobalClick);
    };
  }, [shouldShowElement, onClose]);

  return (
    <>
      <div
        css={popupListContainer}
        className={!shouldShowElement ? "hidden" : ""}
        aria-hidden="true"
        tabIndex={-1}
      >
        <div className="popup-overlay" onClick={onClose}>
          <div
            ref={containerRef}
            className="popup-container"
            onClick={(e): void => e.stopPropagation()}
          >
            <Button size="sm" variant="link" className="popup-close" onClick={onClose}>
              &times;
            </Button>
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

export default PopupChecklist;
